// src/Pages/Sessions/PrevSessions.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import {
  fetchPatientSessions,
  getSessionById,
  deleteSessionById,
  updateSessionById,
} from "../../api";
import { UpdateSession } from "../../Components/Sessions/EditSession";
import { FollowUpSession } from "./FollowUpSession";
import SyncToButton, { hasSyncToAccess } from "../SyncTo/SyncToButton";
import { useRecording } from "../SyncTo/RecordingProvider";
import CaptureReviewBar from "../SyncTo/CaptureReviewBar";
import SessionNoteRail, {
  deriveKind,
  expandNoteRows,
} from "./SessionNoteRail";
import "./EditSession.css";
import "../Accounts/acc-list.css";

/**
 * Previous Notes.
 *
 * The dropdown selects an APPOINTMENT, not a note. Since an appointment can
 * hold several notes, listing every note in the dropdown produced rows that
 * were indistinguishable from one another ("Physio • 15 Sep 2026, 10:00" twice)
 * and no way to see that they belonged together. The appointment's notes now
 * live in the same side rail used when creating them, so reviewing history and
 * writing it work the same way.
 *
 * Ad-hoc notes have no appointment to group under, so each is its own entry.
 */
export function PrevSessions({ patientId }) {
  const [groups, setGroups] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [activeNote, setActiveNote] = useState(null);
  // Which kind of the open document is being shown. For a legacy bundle the
  // rail offers several kinds backed by the same doc, so this — not the
  // document — decides which editor renders.
  const [activeNoteKind, setActiveNoteKind] = useState(null);
  const [draftNoteKind, setDraftNoteKind] = useState(null);
  // See Session.jsx — keying the create editor on the kind alone let React
  // reuse the instance when two notes of the same kind were added in a row.
  const [draftToken, setDraftToken] = useState(0);
  // Set when an ungrouped ad-hoc note is promoted to a sitting, so the note
  // being added joins it even before the refreshed group list arrives.
  const [pendingAdHocGroupId, setPendingAdHocGroupId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const editorRef = useRef(null);
  const recording = useRecording();

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ------------ helpers

  const sortStamp = (s) =>
    new Date(
      s.sessionsAppointmentStart || s.createdAt || s.updatedAt || 0,
    ).getTime();

  const formatWhen = (iso) => {
    if (!iso) return "Unknown date";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "Unknown date";
    return d.toLocaleString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  /**
   * Collapse a flat session list into one row per appointment.
   *
   * Ad-hoc notes key on their own id, so each stands alone — they are genuinely
   * independent events, not a series.
   */
  const buildGroups = useCallback((list) => {
    const byKey = new Map();
    for (const s of list || []) {
      const isAdHoc = !!s.sessionsAdHoc || !s.sessionsAppointmentId;
      // An ad-hoc sitting groups on its own key when it has one. Notes written
      // before that key existed fall back to their own id — a group of one,
      // which is exactly how they already behaved.
      const key = isAdHoc
        ? `adhoc:${s.sessionsAdHocGroupId || s._id}`
        : `appt:${s.sessionsAppointmentId}`;
      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          isAdHoc,
          appointmentId: isAdHoc ? null : String(s.sessionsAppointmentId),
          adHocGroupId: isAdHoc ? s.sessionsAdHocGroupId || null : null,
          serviceName: s.sessionsServiceName || (isAdHoc ? "Ad Hoc" : "—"),
          when: s.sessionsAppointmentStart || s.createdAt || null,
          practiceId: s.sessionsPracticeId,
          resourceId: s.sessionsResourceId || null,
          notes: [],
        });
      }
      const g = byKey.get(key);
      g.notes.push(s);
      // The group's timestamp is its earliest note — the appointment's own time.
      if (sortStamp(s) < new Date(g.when || 0).getTime() || !g.when) {
        g.when = s.sessionsAppointmentStart || s.createdAt || g.when;
      }
    }
    const out = [...byKey.values()];
    for (const g of out) {
      g.notes.sort((a, b) => sortStamp(a) - sortStamp(b));
    }
    out.sort((a, b) => new Date(b.when || 0) - new Date(a.when || 0));
    return out;
  }, []);

  const labelFor = (g) =>
    `${g.serviceName} · ${formatWhen(g.when)}${
      g.notes.length > 1 ? ` (${g.notes.length} notes)` : ""
    }`;

  /**
   * Rail rows for the selected group, plus the unsaved draft when adding.
   *
   * A legacy note holding several kinds expands into one row per kind, so an
   * old appointment with a template AND a handwritten page shows both.
   */
  const railNotes = (g) => {
    const rows = (g?.notes || []).flatMap((n) => expandNoteRows(n));
    if (draftNoteKind) {
      rows.push({
        _id: "__draft__",
        sessionType: draftNoteKind,
        sessionsNoteTitle: "New note",
        isDraft: true,
      });
    }
    return rows;
  };

  // ------------ data

  const load = useCallback(
    async (preferNoteId) => {
      if (!patientId) return;
      setLoading(true);
      try {
        const list = await fetchPatientSessions(patientId);
        const built = buildGroups(list || []);
        setGroups(built);

        // Keep the user where they were across a refresh; otherwise open the
        // most recent appointment's most recent note.
        const wantedGroup =
          (preferNoteId &&
            built.find((g) => g.notes.some((n) => String(n._id) === String(preferNoteId)))) ||
          built.find((g) => g.key === selectedKey) ||
          built[0];
        if (!wantedGroup) {
          setSelectedKey(null);
          setActiveNoteId(null);
          setActiveNote(null);
          return;
        }
        setSelectedKey(wantedGroup.key);
        const wantedNote =
          (preferNoteId &&
            wantedGroup.notes.find((n) => String(n._id) === String(preferNoteId))) ||
          wantedGroup.notes[wantedGroup.notes.length - 1];
        if (wantedNote) {
          const [firstRow] = expandNoteRows(wantedNote);
          setDraftNoteKind(null);
          setActiveNoteId(firstRow._id);
          setActiveNoteKind(firstRow.sessionType);
          setActiveNote(wantedNote);
        }
      } catch (e) {
        console.error("Error loading sessions", e);
        setGroups([]);
        setSelectedKey(null);
        setActiveNoteId(null);
        setActiveNote(null);
      } finally {
        setLoading(false);
      }
    },
    // `selectedKey` is read to preserve the selection but must not re-trigger
    // the fetch, or every pick would refetch the whole history.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [patientId, buildGroups],
  );

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  /** Re-read the group list, leaving the current selection alone. */
  const refreshGroups = useCallback(async () => {
    try {
      const list = await fetchPatientSessions(patientId);
      setGroups(buildGroups(list || []));
    } catch (e) {
      console.error("Error refreshing sessions", e);
    }
  }, [patientId, buildGroups]);

  /**
   * Applying a capture writes a note — often a brand new one, for a recording
   * that was never bound to a session. Reload the sittings so it appears.
   */
  useEffect(() => {
    const handler = (e) => {
      const detail = (e && e.detail) || {};
      if (detail.patientId && String(detail.patientId) !== String(patientId)) return;
      load(detail.sessionId || undefined);
    };
    window.addEventListener("syncto:applied", handler);
    return () => window.removeEventListener("syncto:applied", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const selectedGroup = groups.find((g) => g.key === selectedKey) || null;
  const canAddToGroup = !!selectedGroup;

  // ------------ handlers

  /** Persist the open note before moving off it (see Session.jsx for the rationale). */
  const saveOpenNote = useCallback(async () => {
    const save = editorRef.current?.saveNow;
    if (typeof save !== "function") return null;
    try {
      return await save();
    } catch (err) {
      console.error("Could not save the open note before switching:", err);
      return null;
    }
  }, []);

  const handlePickGroup = async (g) => {
    setPendingAdHocGroupId(null);
    const savedId = await saveOpenNote();
    if (savedId) await refreshGroups();
    setDraftNoteKind(null);
    setSelectedKey(g.key);
    const last = g.notes[g.notes.length - 1];
    const [firstRow] = last ? expandNoteRows(last) : [null];
    setActiveNoteId(firstRow ? firstRow._id : null);
    setActiveNoteKind(firstRow ? firstRow.sessionType : null);
    setActiveNote(last ? await getSessionById(last._id) : null);
  };

  const handleSelectNote = async (row) => {
    if (!row?._id || row._id === "__draft__") return;
    const savedId = await saveOpenNote();
    // The rail's titles / kinds come from the group list, so refresh it when
    // the note we just left actually changed.
    if (savedId) await refreshGroups();
    setDraftNoteKind(null);
    // A split legacy row points at a shared document via realId.
    const docId = row.realId || row._id;
    const full = await getSessionById(docId);
    if (full && full._id) {
      setActiveNoteId(String(row._id));
      setActiveNoteKind(row.sessionType || deriveKind(full));
      setActiveNote(full);
    }
  };

  const onSyncToBeforeOpen = async () => {
    // Persist whatever is open first, so a recording never costs unsaved edits.
    const savedId = await saveOpenNote();
    if (savedId) await load(savedId);
    const practiceId = selectedGroup?.practiceId || activeNote?.sessionsPracticeId;
    if (!practiceId) {
      alert("Could not resolve practice for this recording — please refresh and try again.");
      return { sessionId: null, error: new Error("Practice not resolved") };
    }
    recording?.openModal({
      appointmentId: selectedGroup?.appointmentId || null,
      patientId,
      practiceId,
      patientLabel: selectedGroup ? `${selectedGroup.serviceName} — ${formatWhen(selectedGroup.when)}` : "",
      sessionId: savedId || (activeNote?._id ? String(activeNote._id) : null),
      existingCapturedValues: activeNote?.capturedValues || {},
      onApplied: () => load(activeNote?._id),
    });
    // SyncToButton skips its own openModal when this returns a null sessionId.
    return { sessionId: null };
  };

  /**
   * The sitting key the new note should join.
   *
   * An ad-hoc note written before sittings existed has none, so one is stamped
   * onto it now — that single write is what lets an old standalone note grow
   * into a group. Appointments group on their own id and need nothing.
   */
  const resolveAdHocGroupId = async (g) => {
    if (!g?.isAdHoc) return null;
    if (g.adHocGroupId) return g.adHocGroupId;
    const groupId =
      (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
      `adhoc-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    // The group is a single ungrouped note at this point, but map over its
    // notes rather than assuming that.
    await Promise.all(
      (g.notes || []).map((n) =>
        updateSessionById(n._id, { sessionsAdHocGroupId: groupId }).catch((err) => {
          console.error("Could not group this ad-hoc note:", err);
        }),
      ),
    );
    return groupId;
  };

  const handleAddNote = async (kind) => {
    const savedId = await saveOpenNote();
    const groupId = await resolveAdHocGroupId(selectedGroup);
    if (groupId) setPendingAdHocGroupId(groupId);
    if (savedId || groupId) await refreshGroups();
    setDraftToken((t) => t + 1);
    setDraftNoteKind(kind);
    setActiveNoteId("__draft__");
    setActiveNote(null);
  };

  const handleDeleteNote = async (row) => {
    if (!row?._id || row._id === "__draft__" || row.isSplit) return;
    if (!window.confirm(`Delete "${row.sessionsNoteTitle}"? This cannot be undone.`)) return;
    try {
      await deleteSessionById(row.realId || row._id);
    } catch (_err) {
      alert("Could not delete this note — please try again.");
      return;
    }
    await load();
  };

  // ------------ render

  // Deliberately not an early return any more: a patient can have recordings
  // waiting before a single note exists, and the Review Needed button is the
  // only way to reach them. Bailing out here made those unreachable.
  const noHistory = !loading && groups.length === 0;

  /**
   * SyncTo as the first entry in the rail's Add menu, matching Appointment
   * Notes. SyncToButton renders the item itself so the feature-flag gate and
   * the browser-support check stay in one place; with the flag off it returns
   * null and Add is just the note kinds.
   */
  // null, not an element that renders null: AddNoteDropdown draws its divider
  // only when it actually has a leading item, and it can't tell the difference
  // from the outside.
  const syncToMenuItem = hasSyncToAccess() ? (
    <SyncToButton
      patientId={patientId}
      appointmentId={selectedGroup?.appointmentId || null}
      onBeforeOpen={onSyncToBeforeOpen}
      onApplied={() => load(activeNote?._id)}
      asDropdownItem
    />
  ) : null;

  const noteRail = (
    <SessionNoteRail
      notes={selectedGroup ? railNotes(selectedGroup) : []}
      activeNoteId={activeNoteId}
      onSelect={handleSelectNote}
      onAdd={handleAddNote}
      addLeadingItems={syncToMenuItem}
      onDelete={handleDeleteNote}
      busy={loading}
      isMobile={isMobile}
      // Add sits at the top of the rail now rather than in the toolbar. It
      // needs a group to add to, so it is disabled — not hidden — while
      // nothing is selected; hiding it would shift every row on selection.
      disabled={!canAddToGroup}
    />
  );

  return (
    <div>

      {/* Appointment picker + Add, mirroring the toolbar above the editor when
          notes are being written. */}
      <div className="acc-list d-flex align-items-center gap-2 mb-2 flex-wrap">
        {/* Rendered even with nothing to pick. Hiding it made the toolbar
            change shape between patients, so a patient with no history looked
            like a screen still loading rather than one that is simply empty —
            and there was nothing on screen to say which of the two it was. */}
        <Dropdown className="prev-sessions-dropdown">
          <Dropdown.Toggle
            variant="outline-secondary"
            size="sm"
            className="rounded-0 d-flex align-items-center justify-content-between gap-2"
            style={{ minWidth: isMobile ? "180px" : "340px" }}
          >
            <span className="text-truncate">
              {selectedGroup
                ? labelFor(selectedGroup)
                : noHistory
                  ? "No previous notes"
                  : "Select appointment"}
            </span>
            <i className="fa-solid fa-chevron-down" style={{ fontSize: "0.95rem", flexShrink: 0 }} />
          </Dropdown.Toggle>
          <Dropdown.Menu className="shadow-sm" style={{ minWidth: "340px", maxHeight: "320px", overflowY: "auto" }}>
            {groups.length === 0 && (
              <div className="px-3 py-2 small text-muted">
                {loading ? "Loading…" : "No previous notes for this patient."}
              </div>
            )}
            {groups.map((g) => (
              <Dropdown.Item
                key={g.key}
                active={selectedKey === g.key}
                onClick={() => handlePickGroup(g)}
              >
                <div className="fw-semibold">
                  {g.serviceName}
                  {g.notes.length > 1 && (
                    <span className="badge bg-secondary ms-2">{g.notes.length}</span>
                  )}
                </div>
                <div className="text-muted small border-bottom border-1">
                  {formatWhen(g.when)}
                </div>
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        {/* What's waiting to be written up, and what was thrown away — the two
            standing lists. Each button carries its own label (Review Needed /
            Dismissed): icon-only versions were tried here and users could not
            tell the two apart without hovering.

            Recording is NOT here. It starts a note, so it sits in the rail's
            Add menu beside the other ways to start one, matching Appointment
            Notes. That also settles the empty-patient case on its own: Add is
            disabled until an appointment is selected, so there is no way to
            record against nothing. Review Needed and Dismissed stay because
            they are patient-wide, and a patient whose only history is an
            unreviewed recording is exactly who needs them.

            The whole group is gated on access. Without SyncTo there are no
            captures to review or restore, so the label and both buttons would
            be a permanently empty feature advertising itself — and the buttons
            disable themselves on an empty list, so it would read as broken
            rather than absent. */}
        {hasSyncToAccess() && (
          <>
            <span className="text-primary fw-semibold small">SyncTo:</span>
            {/* Spaced rather than butted together. Labelled buttons size to
                their own text, so the fixed matched width the icon-only pair
                needed is gone with the icons. */}
            <div className="d-flex align-items-center gap-2">
              <CaptureReviewBar
                patientId={patientId}
                onChanged={() => load(activeNote?._id)}
              />
            </div>
          </>
        )}
      </div>

      {draftNoteKind && canAddToGroup ? (
        <FollowUpSession
          key={`prev-new-${selectedGroup.key}-${draftNoteKind}-${draftToken}`}
          ref={editorRef}
          adHoc={selectedGroup.isAdHoc}
          adHocGroupId={selectedGroup.adHocGroupId || pendingAdHocGroupId}
          appointmentId={selectedGroup.appointmentId}
          patientId={patientId}
          serviceName={selectedGroup.serviceName}
          resourceId={selectedGroup.resourceId}
          appointmentStart={selectedGroup.when}
          appointmentStatus={null}
          practiceId={selectedGroup.practiceId}
          activeSection={null}
          sessionType={draftNoteKind}
          onUpdate={(newId, opts) => (opts?.stayPut ? refreshGroups() : load(newId))}
          noteRail={noteRail}
          hideInlineSyncTo
        />
      ) : activeNote ? (
        <UpdateSession
          key={`${activeNote._id}-${activeNoteKind || ""}`}
          ref={editorRef}
          sessionId={activeNote._id}
          patientId={patientId}
          appointmentId={activeNote.sessionsAppointmentId}
          serviceName={activeNote.sessionsServiceName}
          appointmentStart={activeNote.sessionsAppointmentStart}
          appointmentStatus={activeNote.sessionsAppointmentStatus}
          sections={activeNote.sections || {}}
          notepadContentR={activeNote.notepadContent || ""}
          notepadContentType={activeNote.notepadContentType || ""}
          handwritingImageR={activeNote.handwritingImage || null}
          templateId={activeNote.sessionsTemplateId}
          practiceId={activeNote.sessionsPracticeId}
          resourceId={activeNote.sessionsResourceId}
          capturedValuesR={activeNote.capturedValues || {}}
          sessionsTemplateVersionR={activeNote.sessionsTemplateVersion}
          updatedAtR={activeNote.updatedAt || null}
          onSaved={() => load(activeNote._id)}
          inline={true}
          sessionType={activeNoteKind || deriveKind(activeNote)}
          // Never the compatibility multi-tab view: the rail already offers a
          // row per kind, and saving still writes the whole document back, so
          // the kinds not on screen are preserved.
          legacyBundle={false}
          noteRail={noteRail}
          hideInlineSyncTo
        />
      ) : (
        // No note open. Normal for a patient whose only history so far is
        // recordings waiting to be written up — the toolbar above still offers
        // Review Needed, which is how those are reached.
        <div className="d-flex" style={{ marginTop: "4px" }}>
          {!noHistory && (
            <div
              className="border-end p-2"
              style={
                isMobile
                  // Bounded rather than free: the rows carry their titles on
                  // mobile now, and an unconstrained rail would grow to fit
                  // them and squeeze the editor beside it. Titles truncate.
                  ? { width: "40%", maxWidth: 190, flexShrink: 0 }
                  : { width: "210px", flexShrink: 0 }
              }
            >
              {noteRail}
            </div>
          )}
          <div className="p-4 flex-grow-1 text-center text-muted">
            <i className="fa-light fa-notes d-block mb-2" style={{ fontSize: "1.75rem" }} />
            <p className="mb-0">
              {noHistory
                ? "No written notes for this patient yet. Any recordings waiting to be written up are under Review Needed."
                : "Pick an appointment above to read its notes."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
